

import React, { Component } from 'react'
import { connect } from 'react-redux'
import { withRouter } from '../../../common/utils/withRouter'
import _ from 'lodash'
import classNames from 'classnames'
import BigBreadcrumbs from '../../../components/layout/navigation/components/BigBreadcrumbs'
import WidgetGrid from '../../../components/layout/widgets/WidgetGrid'
import JarvisWidget from '../../../components/layout/widgets/JarvisWidget'
import { createStatusBadgeString } from '../../../common/components/diamond/StatusBadge'
import {
  fetchDiamonds,
  clearDiamonds,
  clearSelectedDiamond,
} from '../../../common/actions/diamond'
import { commentedItemsList, getRetailer } from '../../../common/actions/retailer'
import { isStandardRetailer } from '../../../common/utils/retailer'
import {
  statusCodes,
  jewelryTypes,
  settings
} from '../../../common/utils/diamond'
import imageUrl from '../../../common/utils/imageUrl'
import { LIST_PAGE_LENGTH, SERVER } from '../../../common/constants'
import UpgradeAccountButton from '../../../common/components/common/UpgradeAccountButton'
import ClearFormFieldButton from '../../../common/components/common/ClearFormFieldButton'
import EmptyTableDiamonds from '../../../common/components/diamond/EmptyTableDiamonds'
import styles from './List.module.css'
import DataTable from '../../../components/tables/Datatable'
import api from '../../../common/api'
import defaultDiamondImage from '../../../assets/images/image-coming-soon.jpg'

// Gemstones and jewelry come back with cut/clarity/color as null. A renderer
// that throws aborts the whole DataTables draw and leaves the table empty, so
// treat null like 'not-applicable'.
const isNA = value =>
  value == null || value === '' || value === 'not-applicable'
const renderCarat = carat => (isNA(carat) || carat <= 0 ? 'N/A' : carat)
const renderTitle = title =>
  isNA(title) ? 'N/A' : String(title).split('-').map(_.capitalize).join(' ')
const renderClarity = clarity =>
  isNA(clarity) ? 'N/A' : String(clarity).replace(/-/g, ' ').toUpperCase()
const renderUpper = value => (isNA(value) ? 'N/A' : _.toUpper(value))
const renderColor = value =>
  isNA(value) ? 'N/A' : renderUpper(String(value).replace(/-/g, ' '))

const renderImage = img => `
  <div class="${styles.imageColumn}">
    <div class="${styles.expandButton}">+</div>
    <img src="${imageUrl('inventory', img)}" onerror="this.onerror=null; this.src='${defaultDiamondImage}';" />
  </div>
`

// The image lives in a fixed-size box that starts in the isLoading state; the
// img's own onload clears it. Reserving the space keeps the row height stable
// while the file is in flight, so the list no longer steps up and down as a
// page's images arrive one at a time. onerror swaps in the placeholder and lets
// its load event clear isLoading in turn.
const renderCommentImage = img => `
  <div class="${styles.imageColumn}">
    <div class="${styles.imageBox} ${styles.isLoading}">
      <img
        src="${img || defaultDiamondImage}"
        onload="this.parentNode.classList.remove('${styles.isLoading}');"
        onerror="this.onerror=null; this.src='${defaultDiamondImage}';"
      />
    </div>
  </div>
`

const renderCustomTitle = (customTitle, type, row) => `
  <div class="${styles.internalIdColumn}">${customTitle || (row && row.title) || ''}</div>
`

const renderInternalId = internalId => `
  <div class="${styles.internalIdColumn}">${internalId || ''}</div>
`

const isDifferentPage = (currentProps, nextProps) =>
  currentProps.location.query.page !== nextProps.location.query.page

const settingFilterNullItem = {
  value: null,
  text: 'All Settings/Jewelry'
}

const settingFilterItems = [
  {
    value: 'loose',
    text: 'Loose Diamonds'
  },
  {
    value: 'gemstone',
    text: 'Gemstones'
  },
  {
    value: 'mounted',
    text: 'Mounted Jewelry'
  },
  {
    value: 'non-diamond-jewelry',
    text: 'Non-Diamond Jewelry'
  },
  {
    value: 'craft-jewelry',
    text: 'Craft Jewelry'
  },
  {
    value: 'service',
    text: 'Service'
  }
].concat(
  Object.keys(jewelryTypes).map(value => ({
    value,
    text: jewelryTypes[value]
  }))
)

class CommentingSystem extends Component {
  constructor(props) {
    super(props)
    this.state = {
      selectedSetting: settingFilterNullItem,
      commentedData: [],
    }
  }
  componentDidMount() {
    const {
      isAdminUser,
      retailer,
      currentUser,
      getRetailer,
      clearSelectedDiamond,
      commentedItems
    } = this.props
    if (!isAdminUser && !retailer && currentUser) {
      const id = typeof currentUser.get === 'function' ? currentUser.get('id') : currentUser.id
      getRetailer(id)
    }
    clearSelectedDiamond()

    commentedItems().then(() => {
    }).catch(() => {});
    this.handleSpaClick = (e) => {
      const el = e.target.closest('[data-spa-href]')
      if (!el) return
      e.preventDefault()
      const href = el.getAttribute('data-spa-href')
      const productInfoRaw = el.getAttribute('data-product-info')
      if (productInfoRaw) {
        try {
          sessionStorage.setItem('commentProductInfo', decodeURIComponent(productInfoRaw))
        } catch (err) {}
      }
      this.props.router.push(href)
    }
    this._spaListenerNode = null
    if (this.containerNode) {
      this.containerNode.addEventListener('click', this.handleSpaClick)
      this._spaListenerNode = this.containerNode
    }
  }
  componentWillUnmount() {
    if (this.containerNode && this._spaListenerNode === this.containerNode) {
      this.containerNode.removeEventListener('click', this.handleSpaClick)
    }
  }
  componentDidUpdate(prevProps) {
    if (this.containerNode && this.containerNode !== this._spaListenerNode) {
      if (this._spaListenerNode) {
        this._spaListenerNode.removeEventListener('click', this.handleSpaClick)
      }
      this.containerNode.addEventListener('click', this.handleSpaClick)
      this._spaListenerNode = this.containerNode
    }
    const { router, location, diamonds, diamondInProgress } = this.props
    if (
      !diamonds ||
      isDifferentPage(prevProps, this.props) ||
      diamondInProgress
    ) {
      return
    }
    const nextPage = location.query.page
    const page = diamonds.getMeta('pageNumber')
    // Coerce both to strings before comparing — the Laravel API returns
    // current_page as a number while location.query.page is a string, which
    // would otherwise always differ ("1" !== 1) and push the same url on every
    // update until React aborts with "Maximum update depth exceeded".
    if (String(nextPage) !== String(page)) {
      const newLocation = {
        pathname: location.pathname,
        query: Object.assign({}, location.query, { page })
      }
      if (location.query.page) {
        router.push(newLocation)
      } else {
        router.replace(newLocation)
      }
    }
  }
  render() {
    const {
      location,
      isAdminUser,
      retailer,
      diamonds,
      diamondInProgress,
      fetchDiamonds,
      commenteditems
    } = this.props
    if (!isAdminUser && !retailer && !location.query.page) {
      return null
    }
    if (commenteditems == null) {
      return null
    }
    if (commenteditems === false) {
      const errorMsg = this.props.commentedItemsError
      const isForbidden = errorMsg && (errorMsg.message || '').includes('403')
      return (
        <div id="content" className={styles.listWrapper}>
          <div className="row">
            <BigBreadcrumbs
              items={['Commenting System']}
              icon="table"
              className="col-xs-12 col-sm-7 col-md-7 col-lg-4"
            />
          </div>
          <div className="alert alert-danger" style={{ margin: '20px' }}>
            <strong>{isForbidden ? 'Access Denied' : 'Error'}</strong>
            {' — '}
            {isForbidden
              ? 'Forbidden: Admin access required'
              : (errorMsg && errorMsg.message) || 'An unexpected error occurred'}
          </div>
        </div>
      )
    }
    let page = parseInt(location.query.page)
    page = _.isInteger(page) ? Math.max(page, 1) : 1
    const displayStart = (page - 1) * LIST_PAGE_LENGTH
    const columns = [
      { data: 'imageUrl', render: renderCommentImage },
      { data: 'retailerName', isHidden: !isAdminUser, className: 'Retailer' },
      // { data: 'customTitle', defaultContent: '', render: renderCustomTitle },
      { data: 'internalId', defaultContent: '', render: renderInternalId },
      { data: 'statusCode', render: createStatusBadgeString },
      { data: 'price' },
      { data: 'setting', render: this.renderSetting },
      { data: null, render: this.renderEditButtons }
    ].filter(col => !col.isHidden)
    return (
      <div id="content" className={styles.listWrapper} ref={node => { this.containerNode = node }}>
        <div className="row">
          <BigBreadcrumbs
            items={['Commenting System']}
            icon="table"
            className="col-xs-12 col-sm-7 col-md-7 col-lg-4"
          />
          {this.renderActionButtons()}
        </div>
        {diamonds &&
          diamonds.size() === 0 &&
          !diamonds.getMeta('query') &&
          !diamondInProgress
          ? <EmptyTableDiamonds />
          : null}
        <WidgetGrid>
          <div className="row">
            <article className="col-sm-12">
              <JarvisWidget
                editbutton={false}
                togglebutton={false}
                fullscreenbutton={false}
                deletebutton={false}
                color="darken"
              >
                <header>
                  <span className="widget-icon">
                    <i className="fa fa-list" />
                  </span>
                  <h2>Items</h2>
                </header>
                <div>
                  <div className="widget-body no-padding">
                    <DataTable
                      options={{
                        processing: true,
                        serverSide: true,
                        ajax: (query, callback) => {
                          const selectedIds = this.state.selectedIds;
                          fetchDiamonds(
                            this.state.selectedSetting,
                            query,
                            callback,
                            // selectedIds
                          )
                        },
                        lengthChange: false,
                        pageLength: LIST_PAGE_LENGTH,
                        displayStart,
                        search: {
                          search: location.query.query
                        },
                        columns,
                        responsive: { details: { type: 'inline' } }
                      }}
                      searchPlaceholder={`Retailer`}
                      className={classNames(
                        'table table-striped table-bordered table-hover',
                        styles.table
                      )}
                      width="100%"
                      ref={node => {
                        this.cachedDatatable = node && node.cachedDatatable
                      }}
                    >
                      <thead>
                        <tr>
                          <th>Image</th>
                          {isAdminUser && <th className='Retailer'>Retailer</th>}
                          {/* <th>Product Title</th> */}
                          <th>Internal ID</th>
                          <th>Status</th>
                          <th>Price</th>
                          <th>Setting</th>
                          <th
                            className={styles.actionsColumn}
                          >
                            Actions
                          </th>
                        </tr>
                      </thead>
                    </DataTable>
                    {/* {this.renderSettingFilter()} */}
                  </div>
                </div>
              </JarvisWidget>
            </article>
            <div className={styles.sectionHelp}>
              <i className="fa fa-question-circle" />
              <div className={styles.sectionHelpBlurb}>
                <span>
                  <a
                    href="https://intercom.help/gemsby/selling-on-gemsby"
                    target="_blank"
                  >
                    Gemsby Help Center
                  </a>
                </span>
                <a
                  href="https://intercom.help/gemsby/selling-on-gemsby/preparing-your-store/tips-on-preparing-your-store"
                  target="_blank"
                >
                  <strong>Tips on Preparing Your Store</strong>
                </a>
              </div>
            </div>
          </div>
        </WidgetGrid>
      </div>
    )
  }
  renderActionButtons() {
    const { router, isAdminUser, retailer, diamonds } = this.props
    if (!diamonds) {
      return null
    }
    if (isAdminUser || isStandardRetailer(retailer)) {
      return (
        <div className={styles.actionButtons}>
          {/* {isAdminUser &&
            <button
              className={`btn btn-primary ${styles.actionButton}`}
              onClick={() => {
                router.push('/inventory/csvUpload')
              }}
            >
              Bulk Upload (CSV File)
            </button>}
          {this.renderAddButton()} */}
        </div>
      )
    }
    if (diamonds.data.length < 2) {
      return (
        <div className={styles.actionButtons}>
          {this.renderAddButton()}
        </div>
      )
    }
    return (
      <div className={styles.actionButtons}>
        <div className={styles.upgradeMessage}>
          You have reached the account limit, upgrade to{' '}
          <strong>Standard Account</strong> to upload more inventory.
        </div>
        <UpgradeAccountButton className={styles.actionButton} />
      </div>
    )
  }
  renderAddButton() {
    return (
      <button
        className={`btn btn-primary ${styles.actionButton}`}
        onClick={() => {
          this.props.router.push('/inventory/new')
        }}
      >
        Add an Item
      </button>
    )
  }
  renderSettingFilter() {
    return (
      <div className={styles.settingFilter}>
        <div className={classNames('input-group', styles.settingFilterField)}>
          <span className="input-group-addon input-sm">
            <i className="glyphicon glyphicon-filter" />
          </span>
          <div
            className={classNames(
              'form-control',
              'input-sm',
              styles.settingFilterDropdown
            )}
          >
            <button
              type="button"
              className="dropdown-toggle"
              data-toggle="dropdown"
            >
              <span>
                {this.state.selectedSetting.text}
              </span>
              <span className="caret" />
            </button>
            <ul className="dropdown-menu">
              {settingFilterItems.map(item =>
                <li key={item.value}>
                  <a href="#" onClick={this.onFilterSelect(item)}>
                    {item.text}
                  </a>
                </li>
              )}
            </ul>
          </div>
        </div>
        {this.state.selectedSetting.value &&
          <ClearFormFieldButton onClick={this.onFilterClear} />}
      </div>
    )
  }
  renderSetting = (setting, type, row) => {
    if (setting === 'mounted' || setting === 'non-diamond-jewelry') {
      const jewelryType = jewelryTypes[row.jewelryType]
      if (jewelryType) {
        return jewelryType
      }
    }
    return settings[setting] || setting
  }
  renderEditButtons = ({ internalId, statusCode, objectID, imageUrl, price, setting, customTitle, title, retailerName }) => {
    objectID = encodeURIComponent(objectID)
    const productInfo = encodeURIComponent(JSON.stringify({
      customTitle: customTitle || title || '',
      internalId: internalId || '',
      imageUrl: imageUrl || '',
      price: price || '',
      setting: setting || '',
      statusCode: statusCode || '',
      retailerName: retailerName || ''
    }))
    return `<div>
      <a href="/comment/${objectID}" data-spa-href="/comment/${objectID}" data-product-info="${productInfo}">
        <button class="btn btn-primary">
          View Comments
        </button>
      </a>
    </div>`
  }
  setSettingFilter(item) {
    this.setState({ selectedSetting: item })
    setTimeout(() => {
      this.cachedDatatable.search('').draw()
    })
  }
  onFilterSelect = item => {
    return event => {
      event.preventDefault()
      if (!this.cachedDatatable) {
        return
      }
      this.setSettingFilter(item)
    }
  }
  onFilterClear = event => {
    event.preventDefault()
    if (!this.cachedDatatable) {
      return
    }
    this.props.clearDiamonds()
    this.setSettingFilter(settingFilterNullItem)
  }
}

const mapStateToProps = ({ retailer, diamond, login }) => {
  return {
    retailer: retailer.get('selectedRetailer'),
    diamonds: diamond.get('diamonds'),
    diamondInProgress: diamond.get('inProgress'),
    isAdminUser: login.get('isAdminUser'),
    currentUser: login.get('currentUser'),
    commenteditems: retailer.get('commentedItemLists'),
    commentedItemsError: retailer.get('error')
  }
}

const mapDispatchToProps = (dispatch,nextProps) => {
  return {
    async fetchDiamonds(selectedSetting, query, callback) {
      // console.log('fetchDiamonds', selectedSetting, ">>>", query);
      query.setting = selectedSetting.value;
      // if (query.setting && query.setting !== 'loose') {
      //   query.setting = 'mounted';
      //   query.jewelryType = selectedSetting.value;
      // }
      // Derive page from DataTables start/length so pagination works correctly
      const page = Math.floor((query.start || 0) / (query.length || LIST_PAGE_LENGTH)) + 1;
      const filter = query.setting || null
      const retailerName = query.search.value || null;
      try {
        // Fetch reviewed items for the current page
        const filterIdList = await api.getCommentedItemsWithFilter(page, filter, retailerName);

        const recordsTotal = filterIdList.meta ? filterIdList.meta.total : 0;

        callback({
          recordsTotal,
          recordsFiltered: recordsTotal,
          data: (filterIdList.data || [])
            .filter(item => item && item.data)
            .map(item => {
              const attrs = item.data.attributes || {}
              return _.extend({}, attrs, {
                id: item.data.id,
                objectID: attrs.uuid || attrs.internalId,
                internalId: attrs.internalId || attrs.uuid,
                title: attrs.title || '',
                retailerName: attrs.retailerName || '',
                statusCode: attrs.statusCode || null,
                price: attrs.price || '',
                setting: attrs.setting || '',
                imageUrl: attrs.imageUrl || attrs.firstImagePath || '',
              })
            })
        });
      } catch (error) {
        console.error('Error fetching diamonds:', error);
        // You can dispatch error actions here if necessary
        callback({
          recordsTotal: 0,
          recordsFiltered: 0,
          data: []
        });
      }
    },

    getRetailer(retailerId) {
      dispatch(getRetailer(retailerId))
    },
    clearDiamonds() {
      dispatch(clearDiamonds())
    },
    clearSelectedDiamond() {
      dispatch(clearSelectedDiamond())
    },
    // commentedItems() {
    //   dispatch(commentedItemsList())
    // },
    commentedItems: () => dispatch(commentedItemsList())
  }
}

export default withRouter(
  connect(mapStateToProps, mapDispatchToProps)(CommentingSystem)
)
